import { ref, shallowRef } from 'vue'
import type { Router } from 'vue-router'

const MINIMUM_LOADING_DURATION = 800

export interface SwitchingOrganization {
  uid: string
  name: string
  icon_url?: string | null
}

const isSwitching = ref(false)
const switchingOrganization = shallowRef<SwitchingOrganization | undefined>(undefined)
let switchingStartTime: number | null = null
let isRouterHookInstalled = false

export function useOrganizationSwitcher() {
  function startSwitching(organization: SwitchingOrganization): void {
    isSwitching.value = true
    switchingOrganization.value = organization
    switchingStartTime = Date.now()
  }

  function stopSwitching(): void {
    isSwitching.value = false
    switchingOrganization.value = undefined
    switchingStartTime = null
  }

  async function stopSwitchingWithDelay(): Promise<void> {
    if (!switchingStartTime) {
      stopSwitching()
      return
    }

    const elapsed = Date.now() - switchingStartTime
    const remaining = MINIMUM_LOADING_DURATION - elapsed

    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining))
    }

    stopSwitching()
  }

  function installRouterHook(router: Router): void {
    if (isRouterHookInstalled) return
    isRouterHookInstalled = true

    router.afterEach((to, from) => {
      if (isSwitching.value && to.params.organizationId !== from.params.organizationId) {
        stopSwitchingWithDelay()
      }
    })
  }

  return {
    isSwitching,
    switchingOrganization,
    startSwitching,
    stopSwitching,
    installRouterHook,
  }
}
